import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageTransition from "../components/PageTransition";
import SectionHeading from "../components/SectionHeading";
import {
  business,
  services,
  whyChoose,
  team,
  qualifications,
  memberships,
  hours,
  policies,
  aboutHighlights,
} from "../data/content";
const today = new Date().toLocaleDateString("en-AU", { weekday: "long" });

const headline = ["Experience", "true bliss."];

const heroContainer = {
  animate: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const heroLine = {
  initial: { y: "110%" },
  animate: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Home() {
  return (
    <PageTransition>
      {/* Hero */}
      <section className="relative isolate flex min-h-screen items-center justify-center overflow-hidden text-center">
        <motion.img
          src="/ganicwellness/heromassage.jpg"
          alt="Therapist giving a relaxing foot massage at Ganic Wellness"
          animate={{
            scale: [1, 1.15, 1.1, 1.18, 1],
            x: ["0%", "-3%", "2%", "-2%", "0%"],
          }}
          transition={{ duration: 28, ease: "easeInOut", repeat: Infinity }}
          style={{ transformOrigin: "center center", willChange: "transform" }}
          className="absolute inset-0 h-full w-full object-cover"
        />

        <div className="relative z-10 flex flex-col items-center px-6 pt-16">
          <motion.h1
            variants={heroContainer}
            initial="initial"
            animate="animate"
            className="text-display-xl text-cream"
          >
            {headline.map((line) => (
              <span key={line} className="block overflow-hidden">
                <motion.span variants={heroLine} className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-6 max-w-xl text-lg text-cream/85"
          >
            Our soothing wellness treatments rejuvenate your mind and body,
            leaving you feeling completely refreshed and revitalised.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="mt-10 flex flex-col items-center gap-6"
          >
            <Link
              to="/booking"
              className="rounded-full bg-brand-gradient px-8 py-3.5 font-medium text-cream shadow-lg shadow-darkgreen/30 transition-transform hover:-translate-y-0.5"
            >
              Book now
            </Link>
            <a
              href={business.phoneHref}
              className="text-cream/90 hover:text-cream"
            >
              {business.phoneDisplay}
            </a>
          </motion.div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="border-t border-ink/10 bg-cream">
        <div className="container-page py-20 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-display-md text-darkgreen"
          >
            About Ganic Wellness
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mx-auto mt-4 h-px w-16 bg-ink/20"
          />

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              hidden: {},
              show: {
                transition: { staggerChildren: 0.18, delayChildren: 0.15 },
              },
            }}
            className="mt-14 grid gap-14 md:grid-cols-3 md:gap-10"
          >
            {aboutHighlights.map((item) => (
              <motion.div
                key={item.title}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
                  },
                }}
                className="flex flex-col items-center"
              >
                <motion.div
                  initial={{ scale: 0.85, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ scale: 1.04 }}
                  className="h-52 w-52 overflow-hidden rounded-full ring-1 ring-ink/10"
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover"
                  />
                </motion.div>
                <p className="mt-6 text-lg text-ink">{item.title}</p>
                {Array.isArray(item.body) ? (
                  <ul className="mt-3 max-w-xs space-y-1 text-sm text-ink/65 leading-relaxed">
                    {item.body.map((line) => (
                      <li key={line}>· {line}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 max-w-xs text-sm text-ink/65 leading-relaxed">
                    {item.body}
                  </p>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Treatments */}
      <section
        id="treatments"
        className="relative overflow-hidden bg-darkgreen text-cream"
      >
        {/* ambient depth */}
        <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-lightgreen/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-lightgreen/10 blur-3xl" />

        <div className="container-page relative py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-6 border-b border-cream/15 pb-14 md:flex-row md:items-end md:justify-between"
          >
            <div>
              <p className="text-sm font-medium tracking-wide text-lightgreen">
                What we offer
              </p>
              <h2 className="mt-3 max-w-lg text-display-md !text-cream">
                Treatments shaped around what your body needs today
              </h2>
            </div>

            <Link
              to="/booking"
              className="group inline-flex shrink-0 items-center gap-2 text-cream/90 transition-colors hover:text-cream"
            >
              Book a treatment
              <motion.span
                className="inline-block text-lightgreen"
                initial={{ x: 0 }}
                animate={{ x: [0, 4, 0] }}
                transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                →
              </motion.span>
            </Link>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            variants={{
              hidden: {},
              show: {
                transition: { staggerChildren: 0.12, delayChildren: 0.1 },
              },
            }}
          >
            {services.map((service, i) => (
              <motion.div
                key={service.id}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                  },
                }}
                className="group relative border-b border-cream/15"
              >
                <div className="absolute inset-0 -z-10 origin-left scale-x-0 bg-cream/[0.04] transition-transform duration-500 ease-out group-hover:scale-x-100" />

                <div className="grid gap-4 px-2 py-8 md:grid-cols-[auto_1fr_1.4fr] md:items-start md:gap-10 md:px-4">
                  <span className="font-display text-sm text-cream/60">
                    {String(i + 1).padStart(2, "0")}
                  </span>

                  <p className="!text-cream font-display text-2xl transition-colors group-hover:text-lightgreen md:text-3xl">
                    {service.name.replace(" & ", " and ")}
                  </p>

                  <div>
                    <p className="text-cream/80">{service.summary}</p>
                    <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-cream/65">
                      {service.details.map((d) => (
                        <li key={d} className="flex items-center gap-2">
                          <span className="h-1 w-1 rounded-full bg-lightgreen" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      {/* Therapists */}
      <section id="therapists" className="border-t border-ink/10">
        <div className="container-page py-20">
          <SectionHeading
            title="The therapists"
            description="A small team of certified, insured practitioners — each with a different focus, so there's a good fit whatever you're after."
          />
        </div>

        <div>
          {team.map((member, i) => (
            <div key={member.name} className={i % 2 === 1 ? "bg-stone/50" : ""}>
              <div className="container-page grid gap-6 py-10 md:grid-cols-[0.7fr_1.6fr] md:gap-16">
                <div>
                  <p className="font-display text-4xl text-darkgreen">
                    {member.name}
                  </p>
                  <p className="mt-2 text-sm text-lightgreen">{member.years}</p>
                  <p className="mt-4 text-sm text-ink/60">
                    {member.availability}
                  </p>
                </div>
                <div>
                  <p className="max-w-lg text-ink/75 leading-relaxed">
                    {member.bio}
                  </p>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {member.specialties.map((s) => (
                      <li
                        key={s}
                        className="rounded-full border border-darkgreen/25 px-3.5 py-1.5 text-sm text-darkgreen"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="container-page py-16">
          <div className="grid gap-10 md:grid-cols-2 md:gap-16">
            <SectionHeading
              title="Trained, certified, insured"
              description="Beyond individual specialties, the whole team holds current insurance and first aid certification, and most remedial services can be claimed through private health funds."
            />
            <ul className="space-y-3">
              {qualifications.map((q) => (
                <li
                  key={q}
                  className="flex items-start gap-3 border-b border-ink/10 pb-3 text-ink/80"
                >
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-darkgreen" />
                  {q}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Visit / hours */}
      <section id="visit" className="border-t border-ink/10 bg-stone/30">
        <div className="container-page py-20 md:py-24">
          <SectionHeading
            align="center"
            title="Visit us"
            description="East Victoria Park, Perth — open every day of the week."
          />

          <div className="mt-12 grid gap-8 md:grid-cols-2 md:gap-10">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-3xl shadow-sm shadow-ink/5"
            >
              <iframe
                title="Ganic Wellness location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(business.address)}&output=embed`}
                className="h-[420px] w-full border-0 grayscale-[15%]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </motion.div>

            {/* Hours */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={{
                hidden: {},
                show: {
                  transition: { staggerChildren: 0.06, delayChildren: 0.1 },
                },
              }}
              className="overflow-hidden rounded-3xl bg-cream shadow-sm shadow-ink/5"
            >
              {hours.map((h) => {
                const isToday = h.day === today;
                return (
                  <motion.div
                    key={h.day}
                    variants={{
                      hidden: { opacity: 0, x: -12 },
                      show: {
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.45,
                          ease: [0.22, 1, 0.36, 1],
                        },
                      },
                    }}
                    className={`flex items-center justify-between border-b border-ink/10 px-8 py-4 last:border-none ${
                      isToday ? "bg-darkgreen/[0.05]" : ""
                    }`}
                  >
                    <span
                      className={`flex items-center gap-2 ${
                        isToday ? "font-medium text-darkgreen" : "text-ink/70"
                      }`}
                    >
                      {isToday && (
                        <span className="h-1.5 w-1.5 rounded-full bg-lightgreen" />
                      )}
                      {h.day}
                    </span>
                    <span
                      className={isToday ? "text-darkgreen" : "text-ink/60"}
                    >
                      {h.time}
                    </span>
                  </motion.div>
                );
              })}

              <a
                href={business.mapHref}
                className="flex items-center justify-center gap-2 border-t border-ink/10 px-8 py-4 text-sm text-darkgreen underline underline-offset-4 hover:text-lightgreen"
              >
                Get directions
              </a>
            </motion.div>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="bg-stone/40 py-20 text-center">
        <div className="container-page">
          <SectionHeading
            align="center"
            title="Ready for some time to yourself?"
            description="Pick your treatment, therapist and time on our booking page."
          />
          <div className="mt-8 flex flex-wrap items-center justify-center gap-5">
            <Link
              to="/booking"
              className="rounded-full bg-brand-gradient px-7 py-3.5 text-cream shadow-md shadow-darkgreen/25 transition-transform hover:-translate-y-0.5"
            >
              Book now
            </Link>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
